import React, {useEffect, useState} from 'react';
import styles from './EditClientProjectRequest.module.css';
import FileInput from '../../common/input/FileInputPanel';
import TextCard from '../../common/textCard/TextCard';
import {useNavigate, useParams} from "react-router-dom";
import {ProjectType} from "../../../types/project";
import {UpdateProjectRequest} from "../../../types/client";
import {projectRequestInfoAPI, updateProjectRequestAPI} from "../../../services/client";
import ClientLayout from "../layout/ClientLayout";
import {XCircleIcon} from "@heroicons/react/24/solid";
import {useAlert} from "../../common/error/AlertContext";
import Button from "../../common/button/Button";

// 컴포넌트 내부용 타입 (Date 객체 사용)
interface InternalUpdateProjectRequest {
    projectRequestId: number;
    projectName: string;
    deadline: Date;
    startAt: Date;
    explanation: string;
    dataAmount: number;
    projectType: ProjectType;
    deleteFile: boolean;
}

//어드민 프로젝트 추가 요청 수정
const EditClientProjectRequest: React.FC = () => {
    const navigate = useNavigate();
    const {id} = useParams<{ id: string }>();
    const [loading, setLoading] = useState(true);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [sampleName, setSampleName] = useState<string | null>(null);
    const {showAlert} = useAlert();

    // 내부적으로는 Date 객체로 관리
    const [formData, setFormData] = useState<InternalUpdateProjectRequest>({
        projectRequestId: 0,
        projectName: '',
        deadline: new Date(),
        startAt: new Date(),
        explanation: '',
        dataAmount: 0,
        projectType: ProjectType.POLYGON,
        deleteFile: false
    });

    // Date 객체를 YYYY-MM-DD 문자열로 변환 (로컬 시간대 기준)
    //날짜 데이터 형식 변환, 없으면 '-', 있으면 시간 데이터 제외
    const formatDateToString = (date: Date | string): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);

        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Date 객체를 화면 표시용 문자열로 변환 (로컬 시간대 기준)
    const formatDateForDisplay = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // API 전송용 데이터 변환
    const getApiFormData = (): UpdateProjectRequest => ({
        projectRequestId: formData.projectRequestId,
        projectName: formData.projectName,
        deadline: formatDateToString(formData.deadline),
        startAt: formatDateToString(formData.startAt),
        explanation: formData.explanation,
        dataAmount: formData.dataAmount,
        projectType: formData.projectType,
        deleteFile: formData.deleteFile
    });

    useEffect(() => {
        const fetchProjectRequest = async () => {
            if (!id) return;
            try {
                setLoading(true);
                //클라이언트 프로젝트 추가 요청 조회 API
                const response = await projectRequestInfoAPI(parseInt(id));
                setFormData({
                    projectRequestId: response.projectRequestId,
                    projectName: response.projectName,
                    deadline: new Date(response.deadline),
                    startAt: new Date(response.startAt),
                    explanation: response.explanation,
                    dataAmount: response.dataAmount,
                    projectType: response.projectType as ProjectType,
                    deleteFile: false
                });
                if (response.sampleName) {
                    setSampleName(response.sampleName);
                }
            } catch (error) {
                console.error('Error fetching project request:', error);
                navigate('/client/projects');
            } finally {
                setLoading(false);
            }
        };
        fetchProjectRequest();
    }, [id, navigate]);

    //업로드 가능 파일 확장자
    const ALLOWED_IMAGE_TYPES = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/bmp',
        'image/webp'
    ];

    //확장자 검증
    const validateImageFile = (file: File): boolean => {
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            showAlert('이미지 파일만 업로드 가능합니다.', "error");
            return false;
        }
        return true;
    };

    //업로드할 파일 선택
    const handleFileSelect = (file: File) => {
        if (!validateImageFile(file)) return;
        setUploadedFile(file);
    };

    //업로드 파일 삭제
    const handleDeleteSample = () => {
        setFormData(prev => ({
            ...prev,
            deleteFile: true
        }));
        setSampleName(null);
    };

    const handleSubmit = async () => {
        // 프로젝트명 입력 확인
        if (!formData.projectName.trim()) {
            showAlert('프로젝트 이름을 입력해주세요.', "error");
            return;
        }

        try {
            if (id) {
                // Date 객체를 문자열로 변환하여 전송
                const requestData = getApiFormData();

                // 프로젝트 추가 요청 수정 API
                await updateProjectRequestAPI(requestData, uploadedFile);
                showAlert('수정되었습니다.', "info");
                // 프로젝트 추가 요청 조회 페이지로 이동
                navigate(`/client/projects/project-request/${id}`);
            }
        } catch (error) {
            console.error('Error updating project request:', error);
        }
    };

    //프로젝트 정보
    const projectDetails = [
        {
            label: "데이터 수량",
            value: formData.dataAmount.toLocaleString(),
            type: "text" as const,
            onChange: (value: string) => setFormData(prev => ({
                ...prev,
                dataAmount: parseInt(value) || 0
            }))
        },
        {
            label: "가공 방식",
            value: formData.projectType,
            type: "select" as const,
            options: Object.values(ProjectType),
            onChange: (value: string) => setFormData(prev => ({
                ...prev,
                projectType: value as ProjectType
            }))
        }
    ];

    //프로젝트 날짜 정보
    const projectDates = [
        {
            label: "희망 시작일",
            value: formatDateForDisplay(formData.startAt),
            type: "date" as const,
            onChange: (value: string) => setFormData(prev => ({
                ...prev,
                startAt: new Date(value)
            }))
        },
        {
            label: "희망 종료일",
            value: formatDateForDisplay(formData.deadline),
            type: "date" as const,
            onChange: (value: string) => setFormData(prev => ({
                ...prev,
                deadline: new Date(value)
            }))
        }
    ];

    if (loading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    return (
        <ClientLayout>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div>
                        <h1 className={styles.title}>Edit Project Request</h1>
                        <p className={styles.subtitle}>프로젝트 요청 수정</p>
                    </div>
                    <div className={styles.actions}>
                        {/*저장 버튼*/}
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                        >
                            저장
                        </Button>
                        {/*취소 버튼*/}
                        <Button
                            variant="secondary"
                            onClick={() => navigate(`/client/projects/project-request/${id}`)}
                        >
                            취소
                        </Button>
                    </div>
                </div>

                <div className={styles.content}>
                    <div className={styles.leftSection}>
                        <div className={styles.uploadSection}>
                            {/*샘플 데이터 업로드*/}
                            <FileInput
                                onFileSelect={handleFileSelect}
                                accept={ALLOWED_IMAGE_TYPES.join(',')}
                                maxSize={5 * 1024 * 1024}
                                className={styles.calc}
                            />
                            {/*첨부파일 삭제 버튼*/}
                            {sampleName && !formData.deleteFile && (
                                <div className={styles.sampleFile}>
                                    <span className={styles.fileName}>현재 파일: {sampleName}</span>
                                    <button
                                        className={styles.deleteButton}
                                        onClick={handleDeleteSample}
                                        type="button"
                                    >
                                        <XCircleIcon className={styles.deleteIcon}/>
                                    </button>
                                </div>
                            )}
                        </div>

                        {/*프로젝트 추가 요청 정보*/}
                        <div className={styles.detailsCard}>
                            <TextCard items={projectDetails}/>
                            <TextCard items={projectDates}/>
                        </div>
                    </div>

                    <div className={styles.rightSection}>
                        <div className={styles.inputGroup}>
                            {/*프로젝트명 입력*/}
                            <input
                                type="text"
                                className={styles.projectNameInput}
                                placeholder="Project Name"
                                value={formData.projectName}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    projectName: e.target.value
                                }))}
                            />
                        </div>

                        <div className={styles.inputGroup}>
                            {/*프로젝트 설명 입력*/}
                            <textarea
                                className={styles.projectExplanation}
                                placeholder="Project Explanation"
                                value={formData.explanation}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    explanation: e.target.value
                                }))}
                                rows={5}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </ClientLayout>
    );
};

export default EditClientProjectRequest;